import { useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { getOrganizationDashboard } from '@/utils/queryOptions';
import { Center, HStack, Image, Text, VStack } from '@chakra-ui/react';
import logoVariants from '@/assets/img/logo';
import { DASHBOARD_RIGHT_PANEL_WIDTH_PX, MAX_CONTAINER_WIDTH } from '@/constants/dimensions';
import LoadingPage from '@/components/templates/LoadingPage';
import WholenessRingWithOverview from '@/components/organisms/WholenessRingWithOverview';
import OrgDashboardStatsItemBox from '@/modules/organization/OrgDashboardStatsItemBox';
import ArrowIconUp from '@/components/atoms/ArrowIconUp';
import ArrowIconDown from '@/components/atoms/ArrowIconDown';
import UnscreenedRiskHoverIcon from '@/modules/organization/UnscreenedRiskHoverIcon';
import { formatDate } from 'date-fns';
import { useMemo, useState } from 'react';
import ConfirmationModal from '@/components/molecules/ConfirmationModal';
import useSendScreenerReminder from '@/hooks/notifications/useSendScreenerReminder';
import svgIcons from '@/assets/svg/icons';
import WholenessTrends from '@/components/molecules/WholenessTrends';
import PeerSupportDashboardOverview from '@/modules/peer-support/PeerSupportDashboardOverview';
import { useAppStore } from '@/store/useAppStore';
import roles from '@/constants/roles';
import TabButtons from '@/components/atoms/TabButtons';
import OrganizationActiveContractPage from '@/components/organisms/OrganizationActiveContractPage';

const ORG_LOGO_CONTAINER_SIZE = 100;
const ORG_LOGO_PADDING = 20;
const ORG_LOGO_SIZE = ORG_LOGO_CONTAINER_SIZE - ORG_LOGO_PADDING;

const MAX_BAR_HEIGHT = 170;
const BAR_WIDTH = 32;

const OrganizationDashboard = () => {
  const { user } = useAppStore();

  const { data } = useSuspenseQuery(getOrganizationDashboard(user?.user_id));
  const organizationDashboard = data;

  const organizationMonthlySessionsUtilization = useMemo(
    () => [...organizationDashboard.stats.sessionsUtilization.monthly].reverse(),
    [organizationDashboard],
  );

  const isOrgAdmin = user?.roles.includes(roles.orgAdmin);

  const [currentTabSelection, setCurrentTabSelection] = useState<'home' | 'contract'>('home');

  const [isSendReminderConfirmationModalOpen, setIsSendReminderConfirmationModalOpen] = useState(false);

  const {
    mutate: sendScreenerReminder,
    error: sendScreenerReminderError,
    isPending,
  } = useSendScreenerReminder(() => {
    setIsSendReminderConfirmationModalOpen(false);
  });

  return (
    <VStack width={MAX_CONTAINER_WIDTH} paddingBottom={'100px'} gap={0}>
      {/* Actual page content */}
      <VStack width={'100%'} marginBottom={'50px'}>
        {/* Org Logo and Name */}
        <HStack width={'100%'} justifyContent={'space-between'}>
          <HStack gap={'30px'}>
            <Center
              backgroundColor={'extra.white'}
              borderRadius={'50%'}
              boxShadow="0px 0px 12.5px 0px #00417926"
              width={`${ORG_LOGO_CONTAINER_SIZE}px`}
              minWidth={`${ORG_LOGO_CONTAINER_SIZE}px`}
              height={`${ORG_LOGO_CONTAINER_SIZE}px`}
              padding={`${ORG_LOGO_PADDING}px`}
            >
              {organizationDashboard?.logoUrl ? (
                <Image
                  alt="user-avatar-wellness"
                  width={`${ORG_LOGO_SIZE}px`}
                  maxHeight={`${ORG_LOGO_SIZE}px`}
                  src={organizationDashboard?.logoUrl}
                  style={{ objectFit: 'cover' }} // borderRadius: '50%',
                />
              ) : (
                <Center
                  borderRadius={'50%'}
                  border={'5px solid white'}
                  boxShadow={'0px 4px 8px 0px #0E00621A'}
                  width={`${ORG_LOGO_SIZE}px`}
                  height={`${ORG_LOGO_SIZE}px`}
                  backgroundColor={'primary.100'}
                >
                  <Image
                    src={logoVariants.purpleNoText112x112op50pct}
                    width={'30px'}
                    height={'30px'}
                    alt="my-omnia-logo"
                  />
                </Center>
              )}
            </Center>

            <Text variant={'urbanistTitleMedium'}>{organizationDashboard.name}</Text>
          </HStack>

          <Center>
            <TabButtons
              minWidth="120px"
              padding={'0 30px'}
              labelStyle={{ fontSize: '14px', lineHeight: '14px' }}
              options={[
                {
                  labelDisplay: 'WHOLENESS HOME',
                  labelValue: 'home',
                  onClick: () => setCurrentTabSelection('home'),
                },
                { labelDisplay: 'CONTRACT', labelValue: 'contract', onClick: () => setCurrentTabSelection('contract') },
              ]}
              selectedOptionValue={currentTabSelection}
            />
          </Center>
        </HStack>

        {currentTabSelection === 'home' ? (
          <>
            {/* Stats */}
            <HStack justifyContent={'space-between'} marginTop={'40px'} marginBottom={'86px'} width={'100%'}>
              <VStack width={'100%'} gap={'20px'}>
                <HStack width={'100%'} gap={'20px'}>
                  <OrgDashboardStatsItemBox
                    title={`Total\nScreeners`}
                    bottomLeftText={organizationDashboard.stats.totalScreeners.current}
                    bottomRightSmallText="Last 30 days"
                    trendIconJSX={
                      organizationDashboard.stats.totalScreeners.trend === 'DECLINING' ? (
                        <ArrowIconDown />
                      ) : (
                        <ArrowIconUp />
                      )
                    }
                  />
                  <OrgDashboardStatsItemBox
                    title="Total Members Signed in"
                    bottomLeftText={organizationDashboard.stats.totalMembersSignedIn.current}
                    bottomRightSmallText="Last 30 days"
                    trendIconJSX={
                      organizationDashboard.stats.totalMembersSignedIn.trend === 'DECLINING' ? (
                        <ArrowIconDown />
                      ) : (
                        <ArrowIconUp />
                      )
                    }
                  />
                  <OrgDashboardStatsItemBox
                    title="Member Care Sessions"
                    bottomLeftText={organizationDashboard.stats.memberCareSessions.current}
                    bottomRightSmallText="Last 30 days"
                    trendIconJSX={
                      organizationDashboard.stats.memberCareSessions.trend === 'DECLINING' ? (
                        <ArrowIconDown />
                      ) : (
                        <ArrowIconUp />
                      )
                    }
                  />
                  <OrgDashboardStatsItemBox
                    title="Peer Support Requests"
                    bottomLeftText={organizationDashboard.stats.peerSupportRequests.current}
                    bottomRightSmallText="Last 30 days"
                    trendIconJSX={
                      organizationDashboard.stats.peerSupportRequests.trend === 'DECLINING' ? (
                        <ArrowIconDown />
                      ) : (
                        <ArrowIconUp />
                      )
                    }
                  />
                </HStack>

                <HStack width={'100%'} gap={'20px'}>
                  <OrgDashboardStatsItemBox
                    title="Unknown Risk (Unscreened)"
                    bottomLeftText={`${organizationDashboard.stats.unknownRisk}%`}
                    trendIconJSX={isOrgAdmin ? <UnscreenedRiskHoverIcon /> : undefined}
                  />
                  <OrgDashboardStatsItemBox
                    title="Total Employees Male / Female"
                    bottomLeftText={`${organizationDashboard.stats.employeeGender.male}%`}
                    bottomRightLargeText={`${organizationDashboard.stats.employeeGender.female}%`}
                  />
                  <OrgDashboardStatsItemBox
                    title="Training Sessions Delivered"
                    bottomLeftText={organizationDashboard.stats.trainingSessions?.completed ?? 0}
                    bottomRightSmallText={`/ ${organizationDashboard.stats.trainingSessions?.total ?? 0} Total`}
                  />
                  <OrgDashboardStatsItemBox
                    title="Peer Support Certifications"
                    bottomLeftText={organizationDashboard.stats.peerSupportCertifications.current}
                    bottomRightSmallText={`/ ${organizationDashboard.stats.peerSupportCertifications.total} Total`}
                  />
                </HStack>
              </VStack>

              <VStack
                minWidth={DASHBOARD_RIGHT_PANEL_WIDTH_PX}
                height={'276px'}
                backgroundColor={'extra.white'}
                boxShadow="0px 8px 34px 0px #0041791A"
                borderRadius={'14px'}
                padding={'20px'}
              >
                <HStack width={'100%'} justifyContent={'space-between'}>
                  <Text variant={'urbanistBoldLarge'}>Sessions Utilization</Text>
                  <Text variant={'urbanistMediumRegular'} color={'text.mediumGray'}>
                    {organizationDashboard.stats.sessionsUtilization.totalUsed} Used /{' '}
                    {organizationDashboard.stats.sessionsUtilization.totalAvailable} Purchased
                  </Text>
                </HStack>

                <HStack height={'100%'} gap={'15px'}>
                  {organizationMonthlySessionsUtilization.map((sessionMonthData) => (
                    <VStack
                      key={`${sessionMonthData.year}-${sessionMonthData.month}`}
                      height={'100%'}
                      justifyContent={'end'}
                    >
                      <VStack
                        backgroundColor={'primary.500'}
                        borderRadius={'10px'}
                        gap={0}
                        height={
                          organizationDashboard.stats.sessionsUtilization.totalUsed === 0 &&
                          organizationDashboard.stats.sessionsUtilization.totalAvailable === 0
                            ? MAX_BAR_HEIGHT / 2
                            : `${Math.min((MAX_BAR_HEIGHT * sessionMonthData.used) / sessionMonthData.purchased, MAX_BAR_HEIGHT)}px`
                        }
                        width={`${BAR_WIDTH}px`}
                      />
                      <HStack maxWidth={`${BAR_WIDTH + 4}px`} gap="4px">
                        <Text variant={'urbanistSmallBold'} color={'text.mediumGray'}>
                          {formatDate(new Date(2025, sessionMonthData.month - 1), 'MMM')}
                        </Text>
                        <Text variant={'urbanistSmallBold'}>{sessionMonthData.used}</Text>
                      </HStack>
                    </VStack>
                  ))}
                </HStack>
              </VStack>
            </HStack>

            {/* Wholeness Ring with Overview  &  WholenessTrends  &  Peer Support Dashboard Overview  */}
            <VStack width={'100%'} gap={'100px'}>
              <WholenessRingWithOverview
                dashboardInsights={organizationDashboard ? organizationDashboard.insights : undefined}
                dashboardScores={organizationDashboard.score}
                wholenessRingAvatar={organizationDashboard?.logoUrl}
                showCenteredTextOverlay={false}
                subtitle="This graph displays the total scores of your team who have taken the screener over the past 30 days."
                overviewBottomButtonOnClick={isOrgAdmin ? () => setIsSendReminderConfirmationModalOpen(true) : null}
                overviewBottomButtonIconPath={svgIcons.curvedArrowRight}
                overviewBottomButtonLabel="Resend Screener"
                overviewBottomButtonIconSize={16}
                overviewDescription="You’ll start seeing your organization’s progress once your members have completed more screeners."
              />

              <WholenessTrends
                dashboardTrendData={organizationDashboard.insights ? organizationDashboard.trends : null}
              />

              <PeerSupportDashboardOverview />
            </VStack>
          </>
        ) : (
          <OrganizationActiveContractPage />
        )}
      </VStack>

      <ConfirmationModal
        title="Send Screener Reminder"
        description="This will send a reminder to every organization member to retake the screener."
        confirmButtonText="Send"
        isOpen={isSendReminderConfirmationModalOpen}
        onClose={() => setIsSendReminderConfirmationModalOpen(false)}
        confirmLoading={isPending}
        onConfirm={sendScreenerReminder}
        errorText={sendScreenerReminderError?.errors[0].message}
      />
    </VStack>
  );
};

export const Route = createFileRoute('/_authenticated/_organization/organization/dashboard')({
  component: OrganizationDashboard,
  loader: async ({ context }) =>
    context.queryClient.ensureQueryData(getOrganizationDashboard(context.auth?.user?.user_id)),
  // NOTE: Default pending component can be overridden if you want to show a custom pending states. This is just a showcase
  pendingComponent: () => <LoadingPage />,
});
